import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { AddOnPricingVariant } from '@larki/shared/type/pricing';

import Icon from '../../../components/icon/Icon';
import Loading from '../../../components/loading/Loading';
import { Button } from '../../../components/button';
import {
  useGetAddOnPricingQuery,
  useUpdateAddOnMultiplierMutation,
} from '../../../redux/rtk';
import { alertError, alertSuccess } from '../../../redux/actions';
import text from '../../../text';
import { captureError } from '../../../utilities/error';
import ConfirmModal from './ConfirmModal';
import EditableCell from './EditableCell';
import PricingHistory from './PricingHistory';
import { addOnVariantLabel, formatMultiplier } from './addOnLabels';
import { deriveMemberPrice, formatMoney } from './money';

// Mirrors the server validator so typos fail on the button, not as a 400.
const isValidMultiplier = (value: number) =>
  Number.isFinite(value) && value > 0 && Number(value.toFixed(2)) === value;

type AddOnPricingModalProps = {
  show: boolean;
  onHide: () => void;
  categoryId: number;
  icon: string;
  label: string;
};

const AddOnPricingModal = ({
  show,
  onHide,
  categoryId,
  icon,
  label,
}: AddOnPricingModalProps) => {
  const dispatch = useDispatch();
  const { data } = useGetAddOnPricingQuery(categoryId);
  const [updateAddOnMultiplier, { isLoading: isSaving }] =
    useUpdateAddOnMultiplierMutation();

  const [multipliers, setMultipliers] = useState<
    Partial<Record<AddOnPricingVariant, number>>
  >({});
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!data) return;
    setMultipliers(
      Object.fromEntries(data.rows.map((row) => [row.variant, row.multiplier]))
    );
  }, [data]);

  const changedRows = useMemo(
    () =>
      data
        ? data.rows.filter(
            (row) =>
              (multipliers[row.variant] ?? row.multiplier) !== row.multiplier
          )
        : [],
    [data, multipliers]
  );

  const hasInvalidRows = changedRows.some(
    (row) => !isValidMultiplier(multipliers[row.variant] ?? row.multiplier)
  );

  const handleSave = async () => {
    const { single, hasOther } = multipliers;
    // Both values are seeded before the save button becomes clickable.
    if (single == null || hasOther == null) return;
    try {
      await updateAddOnMultiplier({
        categoryId,
        data: { single, hasOther },
      }).unwrap();
      dispatch(
        alertSuccess(text('pricingUpdated'), {
          icon: 'circled-tick',
          className: 'pricing-success-banner',
        })
      );
      setConfirming(false);
      onHide();
    } catch (err) {
      captureError(err);
      setConfirming(false);
      dispatch(alertError(text('pricingUpdateFailed')));
    }
  };

  const memberDiscountPercent = data?.rows[0]?.memberDiscountPercent ?? 0;
  const parentLabel = data?.category.name.endsWith('exterior')
    ? 'Exterior'
    : data?.category.name.endsWith('interior')
      ? 'Interior'
      : '';

  return (
    <>
      <Modal
        centered
        scrollable
        show={show}
        onHide={onHide}
        dialogClassName='PricingModal'
      >
        <Modal.Header closeButton>
          <div className='d-flex align-items-center flex-gap-12'>
            <Icon
              icon={icon}
              hasIconClass={false}
              className='pricing-modal__icon original'
            />
            <h3 className='green-1 m-0'>{label}</h3>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Gate only on missing data so refetches update the table in place. */}
          {!data ? (
            <Loading />
          ) : (
            <>
              <div className='pricing-modal__discount'>
                <span className='label'>{text('memberDiscountPercent')}</span>
                <span className='value'>
                  {memberDiscountPercent.toFixed(2)}%
                </span>
              </div>
              <table className='pricing-modal__table pricing-modal__table--add-on'>
                <thead>
                  <tr>
                    <th style={{ verticalAlign: 'top' }}>
                      {text('pointCloud3D')}
                    </th>
                    <th>
                      {text('multipleOfProduct', { product: parentLabel })}
                    </th>
                    <th>
                      {text('exampleAreaPriceExcGst', {
                        area: String(data.exampleAreaSqm),
                      })}
                    </th>
                    <th>{text('memberPriceExcGst')}</th>
                    <th>{text('memberPriceIncGst')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const multiplier =
                      multipliers[row.variant] ?? row.multiplier;
                    const examplePriceExGst = Math.round(
                      data.exampleBasePriceExGst * multiplier
                    );
                    const { memberPriceExGst, memberPriceIncGst } =
                      deriveMemberPrice(
                        examplePriceExGst,
                        row.memberDiscountPercent,
                        data.gstPercent
                      );
                    return (
                      <tr key={row.variant}>
                        <td>
                          {addOnVariantLabel(data.category.name, row.variant)}
                        </td>
                        <td className='editable'>
                          <EditableCell
                            value={multiplier}
                            display={formatMultiplier(multiplier)}
                            onCommit={(next) =>
                              setMultipliers((prev) => ({
                                ...prev,
                                [row.variant]: next,
                              }))
                            }
                          />
                        </td>
                        <td>{formatMoney(examplePriceExGst)}</td>
                        <td>{formatMoney(memberPriceExGst)}</td>
                        <td>{formatMoney(memberPriceIncGst)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PricingHistory
                categoryId={categoryId}
                categoryName={data.category.name}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className='justify-content-center flex-gap-12'>
          <Button
            variant='primary'
            disabled={changedRows.length === 0 || hasInvalidRows}
            onClick={() => setConfirming(true)}
          >
            {text('updatePricingDefaults')}
          </Button>
          <Button variant='link' onClick={onHide}>
            {text('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={confirming}
        message={text('pricingChangeWarning')}
        loading={isSaving}
        onHide={() => setConfirming(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default AddOnPricingModal;
